export const RECEIPT_TIME_ZONE = "Asia/Yangon";

const RECEIPT_TIME_ZONE_OFFSET = "+06:30";

const toFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const padDatePart = (value) => String(value).padStart(2, "0");

const getReceiptDateParts = (dateValue) => {
  const value = new Date(dateValue);
  if (Number.isNaN(value.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: RECEIPT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value);

  return parts.reduce((result, part) => {
    if (part.type !== "literal") result[part.type] = part.value;
    return result;
  }, {});
};

const toReceiptDateTime = (dateValue, timeValue, fallbackTime) => {
  const time = timeValue || fallbackTime;
  return new Date(dateValue + "T" + time + ":00" + RECEIPT_TIME_ZONE_OFFSET);
};

export const createTodayReceiptFilters = (now = new Date()) => {
  const today = formatReceiptDate(now);
  return {
    dateFrom: today,
    dateTo: today,
    timeFrom: "",
    timeTo: "",
    productSearch: "",
  };
};

export const buildReceiptDateRange = (filters = {}) => {
  const dateFrom = filters.dateFrom || filters.dateTo;
  const dateTo = filters.dateTo || filters.dateFrom;
  if (!dateFrom && !dateTo) {
    return { from: null, to: null };
  }

  const fromDate = dateFrom || formatReceiptDate(new Date());
  const toDate = dateTo || fromDate;
  const from = toReceiptDateTime(fromDate, filters.timeFrom, "00:00");
  const end = filters.timeTo
    ? new Date(toReceiptDateTime(toDate, filters.timeTo, "00:00").getTime() + 60 * 1000)
    : new Date(toReceiptDateTime(toDate, null, "00:00").getTime() + 24 * 60 * 60 * 1000);

  return {
    from: from.toISOString(),
    to: end.toISOString(),
  };
};

const lineText = (line) =>
  [
    line?.name,
    line?.itemName,
    line?.item_name,
    line?.barcode,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const filterReceiptRows = (rows = [], filters = {}) => {
  const search = String(filters.productSearch || "").trim().toLowerCase();
  if (!search) return rows;

  return rows.filter((row) =>
    (row.items || []).some((line) => lineText(line).includes(search)),
  );
};

export const isReceiptEditableToday = (createdAt, now = new Date()) => {
  const created = getReceiptDateParts(createdAt);
  const current = getReceiptDateParts(now);
  if (!created || !current) return false;
  return (
    created.year === current.year &&
    created.month === current.month &&
    created.day === current.day
  );
};

export const normalizeReceiptLine = (line = {}) => {
  const quantity = Math.max(
    1,
    Number.parseInt(line.unit_quantity ?? line.quantity, 10) || 1,
  );
  const unitPrice = Math.max(
    0,
    Math.round(toFiniteNumber(line.unitPrice ?? line.unit_price)),
  );
  const conversionFactor = Math.max(
    1,
    Number.parseInt(line.conversionFactor ?? line.conversion_factor, 10) || 1,
  );

  return {
    id: line.id,
    itemId: line.itemId ?? line.item_id,
    unitId: line.unitId ?? line.product_unit_id ?? null,
    barcode: line.barcode || "",
    name: line.name ?? line.itemName ?? line.item_name ?? "",
    unitName: line.unitName ?? line.unit_name ?? "Unit",
    quantity,
    conversionFactor,
    baseQuantity:
      Number.parseInt(line.baseQuantity ?? line.base_quantity, 10) ||
      quantity * conversionFactor,
    unitPrice,
    catalogUnitPrice: Math.max(
      0,
      Math.round(toFiniteNumber(line.catalogUnitPrice ?? line.catalog_unit_price, unitPrice)),
    ),
    totalPrice: quantity * unitPrice,
  };
};

export const calculateReceiptTotal = (lines = []) =>
  lines.reduce(
    (total, line) =>
      total +
      Math.max(0, Number.parseInt(line.quantity, 10) || 0) *
        Math.max(0, Math.round(toFiniteNumber(line.unitPrice))),
    0,
  );

export const buildReceiptCorrectionPayload = (lines = []) => {
  if (!Array.isArray(lines) || lines.length === 0) {
    throw new Error("Receipt must contain at least one item");
  }

  return lines.map((line) => {
    const quantity = Number.parseInt(line.quantity, 10);
    const unitPrice = Number(line.unitPrice);
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      throw new Error("Receipt quantity must be a whole number greater than zero");
    }
    if (!Number.isSafeInteger(unitPrice) || unitPrice < 0) {
      throw new Error("Receipt price must be a nonnegative whole MMK amount");
    }

    if (line.unitId !== undefined && line.unitId !== null) {
      return {
        unit_id: line.unitId,
        quantity,
        unit_price_override: unitPrice,
      };
    }

    const itemId = line.itemId ?? line.item_id;
    const barcode = String(line.barcode || "").trim();
    if (!itemId || !barcode) {
      throw new Error("Receipt item identity is incomplete");
    }

    return {
      item_id: itemId,
      barcode,
      quantity,
      unit_price_override: unitPrice,
    };
  });
};

export const formatReceiptDate = (date) => {
  const parts = getReceiptDateParts(date);
  if (!parts) return "";
  return parts.year + "-" + padDatePart(parts.month) + "-" + padDatePart(parts.day);
};

export const formatReceiptDateTime = (date) => {
  const parts = getReceiptDateParts(date);
  if (!parts) return "";
  return `${parts.day}/${parts.month}/${parts.year}, ${parts.hour}:${parts.minute} MMT`;
};
